import {
  LitElement,
  html,
  css,
  ifDefined,
  classMap,
  styles,
} from '@hh/global.js'

class HHCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    position: {
      type: String,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
    open: {
      type: Boolean,
      reflect: true,
    },
    error: {
      type: Boolean,
      reflect: true,
    },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    defaultUnselected: {
      type: Boolean,
      reflect: true,
      attribute: 'default-unselected',
    },
    labelVisuallyHidden: {
      type: Boolean,
      reflect: true,
      attribute: 'label-visually-hidden',
    },
    selected: {
      type: Boolean,
      reflect: true,
    },
    _currentOption: {
      state: true,
    },
  }

  constructor() {
    super()
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.uuid = Math.random() * (1000000 - 1) + 1
    this.position = 'bottom'
    this._active = false
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this.handleBodyClick)
    document.addEventListener('focusin', this.handleDocumentFocusIn.bind(this))
    this.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
    document.removeEventListener('focusin', this.handleFocusIn)
    this.removeEventListener('keydown', this.handleKeydown)
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  toggle(e = false) {
    if (this.disabled) return
    if (e && e.type === 'keyup' && !~['ESCAPE'].indexOf(e.code.toUpperCase())) {
      return true
    }

    this._active = !this._active
    this.open = this._active
  }

  selectValue(e, value) {
    e.preventDefault()
    this._selected = value
    ;(this.querySelector('[slot="input"]') || {}).value = value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          value,
        },
      })
    )
    this.toggle()
  }

  get selectedLabel() {
    const option = this.options.find(
      (option) => option.value === this._selected
    )

    if (!option && this.defaultUnselected) return ''

    if (!option) return 'Invalid selected option'
    this.selected = true
    return option.label
  }

  handleKeydown(e) {
    if (e.key.toUpperCase() === 'ESCAPE') {
      e.preventDefault()
      e.stopPropagation()
      this._active = false
      this.open = false
      this.button.focus({ preventScroll: true })
    }

    if (e.code.toUpperCase() == 'SPACE' || e.key === ' ') {
      if (this._active) {
        e.preventDefault()
        e.stopPropagation()
      }

      this.selectValue(e, this._currentOption.dataset.optionValue)
      this.button.focus({ preventScroll: true })
      return
    }

    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      const allOptions = this._getAllOptions()
      const currentIndex = allOptions.indexOf(this._currentOption)
      let newIndex = Math.max(0, currentIndex)

      // Prevent scrolling
      e.preventDefault()

      if (!this._active) {
        this.toggle(e)
        if (this._currentOption) {
          this._currentOption.focus({ preventScroll: true })
          return
        }
      }

      if (e.key === 'ArrowDown') {
        newIndex = currentIndex + 1
      } else if (e.key === 'ArrowUp') {
        newIndex = currentIndex - 1
      }

      this._setCurrentOption(allOptions[newIndex])
    }
  }

  _getAllOptions() {
    return [...this.renderRoot?.querySelectorAll('.link')]
  }

  _getFirstOption() {
    return this.renderRoot?.querySelector('.link')
  }

  _setCurrentOption(option) {
    const allOptions = this._getAllOptions()

    // Clear selection
    allOptions.forEach((el) => {
      el.tabIndex = -1
    })

    // Select the target option
    if (option) {
      this._currentOption = option
      option.tabIndex = 0
      setTimeout(() => {
        option.focus()
      }, 100)
    }
  }

  handleDocumentFocusIn(e) {
    // Close when focusing out of the select
    const path = e.composedPath()
    if (this && !path.includes(this)) {
      this._active = false
      this.open = false
    }
  }

  handleEnterKey(e) {
    if (e.key !== 'Enter') return
    this.button.focus({ preventScroll: true })
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  get button() {
    return this.renderRoot?.querySelector('button') ?? null
  }

  render() {
    const allOptions = this._getAllOptions()
    const currentIndex = allOptions.indexOf(this._currentOption)

    return html`<div>
      <div class="container">
        <div class="label-wrapper" @click=${this.toggle}>
          <div
            id="${this.uuid}-label"
            class=${classMap({
              'visually-hidden': this.labelVisuallyHidden,
            })}
          >
            <slot name="title"></slot>
          </div>
          <button
            type="button"
            class="button"
            aria-expanded="${this._active}"
            aria-controls="${this.uuid}-list"
            aria-describedby="${this.uuid}-label"
          >
            <span class="button__label">${this.selectedLabel}</span>
            <slot name="icon"></slot>
          </button>
        </div>

        <slot name="input"></slot>
        <ul
          id="${this.uuid}-list"
          class="${classMap({
            list: true,
            'is-active': this._active,
            'position-top': this.position === 'top',
          })}"
          @keyup="${this.toggle}"
          aria-hidden="${!this._active}"
          role="listbox"
          aria-activedescendant="${this.uuid}-list-${currentIndex}"
        >
          ${this.options.map((option, idx) => {
            this.lang && (option.lang = option.value)
            return html`<li
              class="${classMap({
                option: true,
                'is-active': option.value === this._selected,
              })}"
              role="option"
              aria-selected="${option.value === this._selected}"
              id="${this.uuid}-list-${idx}"
            >
              <a
                @click="${(e) => this.selectValue(e, option.value)}"
                @keyup="${this.handleEnterKey}"
                data-option-value="${option.value}"
                class="link"
                href="#"
                hreflang="${ifDefined(option.lang)}"
                lang="${ifDefined(option.lang)}"
              >
                ${this.sanitize(option.label)}
              </a>
            </li>`
          })}
        </ul>
      </div>
      <slot name="error"></slot>
      <slot name="hint"></slot>
    </div>`
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host {
        width: 100%;
      }

      .container {
        position: relative;
      }

      :host([disabled]) .container {
        background-color: var(--hh-color-input-disabled, #f5f5f5);
      }

      .label-wrapper {
        cursor: pointer;
      }

      .button {
        align-items: center;
        background: transparent;
        border: none;
        color: var(--hh-color-text);
        border-bottom: var(--hh-border-width, 1px) solid currentColor;
        cursor: pointer;
        display: flex;
        font: var(--hh-body-font);
        height: var(--hh-height, 35px);
        justify-content: space-between;
        padding: 0;
        width: 100%;
        transition: border var(--hh-duration-default, 300ms);
      }

      .label-wrapper:is(:hover) .button,
      .button:is(:focus-visible, :active) {
        border-bottom: var(--hh-border-active-width, 2px) solid currentColor;
      }

      :host([error]) .button {
        border-color: var(--hh-color-error);
      }

      :host([disabled]) .button {
        color: var(--hh-color-subdued);
        border: none;
      }

      .button__label {
        position: relative;
        top: 8px;
      }

      ::slotted([slot='button']),
      ::slotted([slot='icon']) {
        align-items: center;
        display: flex;
        height: 100%;
        color: currentColor;
      }

      ::slotted([slot='icon']) {
        margin: 1rem 0 0 3rem;
        transition: transform var(--hh-duration-default, 300ms);
      }

      :host([open]) ::slotted([slot='icon']) {
        transform: rotate(180deg);
      }

      ::slotted([slot='title']) {
        color: var(--hh-color-subdued);
        font-size: 0.85em;
        margin: 0;
        position: absolute;
        inset: 0;
      }

      :host([default-unselected]) ::slotted([slot='title']) {
        font: var(--hh-body-font);
        color: var(--hh-color-text);
        inset: 2rem 0px 0px;
        transform: translateY(0px);
        transition: transform var(--hh-duration-medium, 300ms),
          font-size var(--hh-duration-medium, 300ms);
      }

      :host([open][default-unselected]) ::slotted([slot='title']) {
        color: var(--hh-color-subdued, #ccc);
      }

      :host([open][default-unselected]) .button__label {
        visibility: hidden;
      }

      :host([disabled]) ::slotted([slot='title']) {
        color: var(--hh-color-subdued);
      }

      :host([selected][default-unselected]:not([open]))
        ::slotted([slot='title']) {
        font-size: var(--hh-minified-font-size, 1rem);
        color: var(--hh-color-subdued, #ccc);
        transform: translateY(-1.6rem);
      }

      .list {
        background-color: var(--hh-background, white);
        border: var(--hh-border-width, 1px) solid
          var(--hh-border-color, currentColor);
        display: block;
        margin: 0;
        max-height: var(--hh-options-max-height, 19rem);
        max-width: var(--hh-options-max-width, 22rem);
        min-height: var(--hh-options-min-height, 8.2rem);
        min-width: var(--hh-options-min-width, 12rem);
        opacity: 0;
        overflow-y: auto;
        padding-left: 0;
        position: absolute;
        transform: translateY(-0.2rem);
        transition: visibility var(--hh-duration-default, 300ms)
            var(--hh-duration-default, 300ms),
          opacity var(--hh-duration-default, 300ms),
          transform var(--hh-duration-default, 300ms);
        visibility: hidden;
        width: 100%;
        z-index: 1;
      }

      .list.position-top {
        bottom: 100%;
      }

      .list.is-active {
        opacity: 1;
        transition: transform var(--hh-duration-default, 300ms) 1ms,
          opacity var(--hh-duration-default, 300ms) 1ms, visibility 1ms;
        visibility: visible;
      }

      .option {
        list-style: none;
      }

      .option.is-active {
        opacity: 1;
      }

      .link {
        color: inherit;
        display: block;
        padding: var(--hh-link-padding, 1em);
        text-decoration: none;
      }

      .link:is(:hover, :focus-visible) {
        background-color: var(--hh-background-hover, #f5f5f5);
      }

      ::slotted([slot='hint']),
      ::slotted([slot='error']) {
        display: block;
        margin-top: var(--hh-hint-margin-top, 0.8rem);
        font: var(--hh-hint-font);
      }

      ::slotted([slot='error']) {
        color: var(--hh-color-error, #f44336);
      }
    `,
  ]
}

customElements.define('hh-custom-select', HHCustomSelect)
